import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, Image, TouchableOpacity, Modal, StyleSheet, Platform, ScrollView, TextInput, ActivityIndicator, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, limit, orderBy, collectionGroup, onSnapshot, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { useUser } from './LoginPersistence';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { InviteCodes } from './InviteCodes';
import MessagingComponent from './MessagingComponent';
import { CommunityMemberUpgradeForm } from './CommunityMemberUpgradeForm';
import { getResponsiveValue } from '../styles/breakpoints';
import Constants from 'expo-constants';

const CommunityModal = () => {
  const { user } = useUser();
  const navigation = useNavigation();
  const db = getFirestore();
  
  // Community Modal Context
  const {
    showModal: showActivityLog,
    setShowModal,
    activeTab,
    activeDiscussionTab,
    activeInvitationTab,
    closeModal,
    setActiveTab,
    setActiveDiscussionTab,
    setActiveInvitationTab
  } = useCommunityModal();

  // Activity Log states
  const [activityData, setActivityData] = useState([]);
  const [userActivityData, setUserActivityData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [allPostsAnonymized, setAllPostsAnonymized] = useState(false);
  const [isAnonymizing, setIsAnonymizing] = useState(false);
  
  // Organization Invitations states
  const [organizationInvitations, setOrganizationInvitations] = useState([]);
  const [organizationInvitationsHistory, setOrganizationInvitationsHistory] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  
  // Messaging sender states (user or organization)
  const [ownedOrganizations, setOwnedOrganizations] = useState([]);
  const [selectedMessagingSender, setSelectedMessagingSender] = useState(null); // null = user, or organization ID
  const [showSenderDropdown, setShowSenderDropdown] = useState(false);
  // Unread counts per sender (user + each organization)
  const [senderUnreadCounts, setSenderUnreadCounts] = useState({}); // { userId: count, orgId: count }
  
  // Modal states for success/error messages
  const [showSuccessModal, setShowSuccessModal] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(null);
  const [showCommunityMemberSuccessModal, setShowCommunityMemberSuccessModal] = useState(false);

  // Activity log data loading
  useEffect(() => {
    if (user?.uid) {
      loadUserActivityData(); // Load user's own submissions
      
      // If admin, also load all users' data
      if (user.userType === 'admin') {
        loadAllUsersActivityData();
      }
      
      // Refresh every 30 seconds to catch new posts
      const interval = setInterval(() => {
        loadUserActivityData();
        if (user.userType === 'admin') {
          loadAllUsersActivityData();
        }
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  // Listen for unread message counts in real-time
  // Calculate unread for both user and all owned organizations
  useEffect(() => {
    if (!user?.uid) {
      setTotalUnreadCount(0);
      setSenderUnreadCounts({});
      return;
    }

    // Get all IDs to check unread counts for (user + all owned organizations)
    const allSenderIds = [user.uid, ...ownedOrganizations.map(org => org.id)];
    
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains-any', allSenderIds)
    );

    const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
      let totalUnread = 0;
      const unreadCountsBySender = {};
      
      // Initialize all sender counts to 0
      allSenderIds.forEach(senderId => {
        unreadCountsBySender[senderId] = 0;
      });
      
      snapshot.docs.forEach(doc => {
        const conversationData = doc.data();
        // Sum unread counts for user and all owned organizations
        allSenderIds.forEach(senderId => {
          const unreadCount = conversationData.unreadCounts?.[senderId] || 0;
          unreadCountsBySender[senderId] = (unreadCountsBySender[senderId] || 0) + unreadCount;
          totalUnread += unreadCount;
        });
      });
      
      setTotalUnreadCount(totalUnread);
      setSenderUnreadCounts(unreadCountsBySender);
    }, (error) => {
      console.error('🔔 Error listening to conversations:', error);
      setTotalUnreadCount(0);
      setSenderUnreadCounts({});
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid, ownedOrganizations, db]);

  // Load all users' activity data (for admin tab)
  const loadAllUsersActivityData = async () => {
    try {
      setIsLoading(true);
      const db = getFirestore();

      // Get timestamp for 7 days ago for recent messages only
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      // Use collection group to query ALL discussions across ALL venues in one go!
      const discussionsQuery = query(
        collectionGroup(db, 'discussions'),
        where('timestamp', '>=', sevenDaysAgo), // Only recent messages
        orderBy('timestamp', 'desc'),
        limit(100) // Total limit across all venues
      );
      
      const querySnapshot = await getDocs(discussionsQuery);

      const venueCache = {};
      const allActivity = [];

      for (const doc of querySnapshot.docs) {
        const messageData = doc.data();
        const venueId = doc.ref.parent.parent.id;
        
        // Cache venue names to avoid repeated queries
        if (!venueCache[venueId]) {
          try {
            const venueDoc = await getDoc(doc.ref.parent.parent);
            venueCache[venueId] = venueDoc.exists() ? venueDoc.data().name : 'Unknown Venue';
          } catch (venueError) {
            console.warn(`🚀 Error fetching venue ${venueId}:`, venueError);
            venueCache[venueId] = 'Unknown Venue';
          }
        }

        const messageTime = messageData.timestamp?.toDate() || 
                           (messageData.timestamp?.seconds ? new Date(messageData.timestamp.seconds * 1000) : new Date());

        allActivity.push({
          id: doc.id,
          venueId: venueId,
          venueName: venueCache[venueId],
          ...messageData,
          timestamp: messageTime,
          text: messageData.text || messageData.message || messageData.content || 'No message content',
          username: messageData.username || messageData.user || messageData.displayName || 'Anonymous',
          uniqueKey: `${venueId}-${doc.id}`
        });
      }

      setActivityData(allActivity);
      
    } catch (error) {
      console.error('🚀 Error loading admin activity data:', error);
      setActivityData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load current user's activity data (for activity log tab)
  const loadUserActivityData = async () => {
    try {
      const db = getFirestore();

      // Get timestamp for 30 days ago for user's own messages
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Query discussions where the user is the author
      const userDiscussionsQuery = query(
        collectionGroup(db, 'discussions'),
        where('userId', '==', user?.uid), // Filter by current user
        where('timestamp', '>=', thirtyDaysAgo),
        orderBy('timestamp', 'desc'),
        limit(50) // Limit for user's own posts
      );
      
      const querySnapshot = await getDocs(userDiscussionsQuery);

      const venueCache = {};
      const userActivity = [];

      for (const doc of querySnapshot.docs) {
        const messageData = doc.data();
        const venueId = doc.ref.parent.parent.id;
        
        // Cache venue names
        if (!venueCache[venueId]) {
          try {
            const venueDoc = await getDoc(doc.ref.parent.parent);
            venueCache[venueId] = venueDoc.exists() ? venueDoc.data().name : 'Unknown Venue';
          } catch (venueError) {
            console.warn(`📊 Error fetching venue ${venueId}:`, venueError);
            venueCache[venueId] = 'Unknown Venue';
          }
        }

        const messageTime = messageData.timestamp?.toDate() || 
                           (messageData.timestamp?.seconds ? new Date(messageData.timestamp.seconds * 1000) : new Date());

        userActivity.push({
          id: doc.id,
          venueId: venueId,
          venueName: venueCache[venueId],
          ...messageData,
          timestamp: messageTime,
          text: messageData.text || messageData.message || messageData.content || 'No message content',
          username: messageData.username || messageData.user || messageData.displayName || 'You',
          isAnonymous: messageData.isAnonymous || false,
          uniqueKey: `${venueId}-${doc.id}`
        });
      }

      setUserActivityData(userActivity);

    } catch (error) {
      console.error('📊 Error loading user activity data:', error);
      setUserActivityData([]);
    }
  };

  // Filter admin activity data based on search query
  const getFilteredAdminActivity = () => {
    if (!searchQuery.trim()) return activityData;
    
    const query = searchQuery.toLowerCase();
    return activityData.filter(item => 
      item.venueName.toLowerCase().includes(query) ||
      item.username.toLowerCase().includes(query) ||
      item.text.toLowerCase().includes(query)
    );
  };

  // Filter user activity data based on search query
  const getFilteredUserActivity = () => {
    if (!searchQuery.trim()) return userActivityData;
    
    const query = searchQuery.toLowerCase();
    return userActivityData.filter(item => 
      item.venueName.toLowerCase().includes(query) ||
      item.username.toLowerCase().includes(query) ||
      item.text.toLowerCase().includes(query)
    );
  };

  // Format unread count for display
  const formatUnreadCount = (count) => {
    if (count === 0) return null;
    if (count >= 100) return '99+';
    return count.toString();
  };

  // Navigate to specific venue discussion
  const handleActivityClick = (activity) => {
    setShowModal(false);
    navigation.navigate('Venue', {
      venueId: activity.venueId,
      scrollToDiscussion: true // Opens Community section with DiscussionBoard
    });
  };

  // Load anonymization preference
  useEffect(() => {
    const loadAnonymizationPreference = async () => {
      if (!user?.uid) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setAllPostsAnonymized(data.allPostsAnonymized ?? false);
        }
      } catch (error) {
        console.error('Error loading anonymization preference:', error);
      }
    };
    loadAnonymizationPreference();
  }, [user?.uid, db]);

  // Load organization invitations (pending and history)
  const loadOrganizationInvitations = useCallback(async () => {
    if (!user?.email) return;
    try {
      setIsLoadingInvitations(true);
      
      // Load pending invitations
      const pendingQuery = query(
        collection(db, 'organization_invitations'),
        where('inviteeEmail', '==', user.email),
        where('status', '==', 'pending')
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingInvites = [];
      pendingSnapshot.forEach(doc => {
        pendingInvites.push({ id: doc.id, ...doc.data() });
      });
      setOrganizationInvitations(pendingInvites);
      
      // Load accepted/rejected invitations (history)
      const historyQuery = query(
        collection(db, 'organization_invitations'),
        where('inviteeEmail', '==', user.email),
        where('status', 'in', ['accepted', 'rejected'])
      );
      const historySnapshot = await getDocs(historyQuery);
      const historyInvites = [];
      historySnapshot.forEach(doc => {
        historyInvites.push({ id: doc.id, ...doc.data() });
      });
      // Sort by date (most recent first)
      historyInvites.sort((a, b) => {
        const dateA = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      setOrganizationInvitationsHistory(historyInvites);
    } catch (error) {
      console.error('Error loading organization invitations:', error);
      setOrganizationInvitations([]);
      setOrganizationInvitationsHistory([]);
    } finally {
      setIsLoadingInvitations(false);
    }
  }, [user?.email, db]);

  useEffect(() => {
    if (user?.email) {
      loadOrganizationInvitations();
      
      // Refresh every 30 seconds to catch new invitations
      const interval = setInterval(() => {
        loadOrganizationInvitations();
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      setOrganizationInvitations([]);
    }
  }, [user?.email, loadOrganizationInvitations]);

  // Load organizations owned by the user
  const loadOwnedOrganizations = useCallback(async () => {
    if (!user?.uid) {
      setOwnedOrganizations([]);
      setSelectedMessagingSender(null);
      return;
    }
    try {
      const orgsQuery = query(
        collection(db, 'organizations'),
        where('ownerId', '==', user.uid)
      );
      const snapshot = await getDocs(orgsQuery);
      const orgs = [];
      snapshot.forEach(doc => {
        orgs.push({ id: doc.id, ...doc.data() });
      });
      setOwnedOrganizations(orgs);
      // Reset to user if no organizations or selected org no longer exists
      if (selectedMessagingSender && !orgs.find(o => o.id === selectedMessagingSender)) {
        setSelectedMessagingSender(null);
      }
    } catch (error) {
      console.error('Error loading owned organizations:', error);
      setOwnedOrganizations([]);
    }
  }, [user?.uid, db, selectedMessagingSender]);

  useEffect(() => {
    loadOwnedOrganizations();
  }, [loadOwnedOrganizations]);

  // Close dropdown when switching tabs
  useEffect(() => {
    setShowSenderDropdown(false);
  }, [activeDiscussionTab, activeInvitationTab]);

  const handleAcceptInvitation = async (invitationId, organizationId) => {
    if (!user?.uid) return;
    try {
      const invitationRef = doc(db, 'organization_invitations', invitationId);
      const orgRef = doc(db, 'organizations', organizationId);
      
      const orgDoc = await getDoc(orgRef);
      if (!orgDoc.exists()) {
        setShowErrorModal('Organization not found');
        return;
      }

      const orgData = orgDoc.data();
      const currentMembers = orgData.members || [];
      
      // Check max members per organization
      const maxMembersPerOrg = parseInt(Constants.expoConfig?.extra?.maxMembersPerOrg || process.env.EXPO_PUBLIC_MAX_MEMBERS_PER_ORG || '0', 10);
      if (maxMembersPerOrg > 0 && currentMembers.length >= maxMembersPerOrg) {
        setShowErrorModal(`This organization has reached the maximum number of members (${maxMembersPerOrg}). You cannot join at this time.`);
        return;
      }

      // Check if user is already a member
      if (currentMembers.includes(user.uid)) {
        setShowErrorModal('You are already a member of this organization.');
        // Update invitation status anyway
        await updateDoc(invitationRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
        loadOrganizationInvitations();
        return;
      }

      // Check max organizations per user
      const maxOrgsPerUser = parseInt(Constants.expoConfig?.extra?.maxOrgsPerUser || process.env.EXPO_PUBLIC_MAX_ORGS_PER_USER || '0', 10);
      if (maxOrgsPerUser > 0) {
        // Count current organizations (owned + member)
        const ownedQuery = query(
          collection(db, 'organizations'),
          where('ownerId', '==', user.uid)
        );
        const memberQuery = query(
          collection(db, 'organizations'),
          where('members', 'array-contains', user.uid)
        );
        
        const [ownedSnapshot, memberSnapshot] = await Promise.all([
          getDocs(ownedQuery),
          getDocs(memberQuery)
        ]);

        // Get unique organization IDs (user might be both owner and member of same org)
        const orgIds = new Set();
        ownedSnapshot.forEach(doc => orgIds.add(doc.id));
        memberSnapshot.forEach(doc => orgIds.add(doc.id));

        const currentOrgCount = orgIds.size;
        
        if (currentOrgCount >= maxOrgsPerUser) {
          setShowErrorModal(`You have reached the maximum number of organizations (${maxOrgsPerUser}). Please leave an organization before joining another one.`);
          return;
        }
      }

      // All checks passed - add user to organization
      const updatedMembers = [...currentMembers, user.uid];

      await updateDoc(orgRef, {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      await updateDoc(invitationRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });

      // Add organization to user's organizations field
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userDocData = userDoc.exists() ? userDoc.data() : {};
      const currentOrganizations = userDocData.organizations || {};
      
      await updateDoc(userRef, {
        organizations: {
          ...currentOrganizations,
          [organizationId]: {
            role: 'member',
            joinedAt: serverTimestamp()
          }
        }
      });

      loadOrganizationInvitations();
      // View will automatically update showing the invitation in history
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setShowErrorModal('Failed to accept invitation. Please try again.');
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    try {
      const invitationRef = doc(db, 'organization_invitations', invitationId);
      await updateDoc(invitationRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });
      loadOrganizationInvitations();
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      setShowErrorModal('Failed to reject invitation. Please try again.');
    }
  };

  // Bulk anonymize/un-anonymize all posts
  const handleBulkAnonymizeToggle = async () => {
    if (!user?.uid) return;
    
    try {
      const newAnonymizedState = !allPostsAnonymized;
      
      // Show loading indicator
      setIsAnonymizing(true);

      // Query all discussion posts by this user
      const discussionsQuery = query(
        collectionGroup(db, 'discussions'),
        where('userId', '==', user.uid)
      );

      const querySnapshot = await getDocs(discussionsQuery);
      
      if (querySnapshot.empty) {
        setIsAnonymizing(false);
        return;
      }

      // Use batch writes
      let batch = writeBatch(db);
      let batchCount = 0;
      const batchSize = 500;
      let totalUpdated = 0;

      // Get user data
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userDataDoc = userDoc.exists() ? userDoc.data() : null;
      
      const DEFAULT_PROFILE_PHOTOS = [
        require('../../assets/profile_photos/koi.png'),
        require('../../assets/profile_photos/bear.png'),
      ];
      const getRandomDefaultPhoto = () => {
        const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
        return DEFAULT_PROFILE_PHOTOS[randomIndex];
      };

      for (const docSnapshot of querySnapshot.docs) {
        const postRef = docSnapshot.ref;
        
        if (newAnonymizedState) {
          batch.update(postRef, {
            isAnonymous: true,
            username: 'Anonymous',
            userAvatar: getRandomDefaultPhoto()
          });
        } else {
          const originalUsername = userDataDoc?.username || userDataDoc?.firstName || 'Anonymous';
          const originalAvatar = userDataDoc?.photoURL || getRandomDefaultPhoto();
          batch.update(postRef, {
            isAnonymous: false,
            username: originalUsername,
            userAvatar: originalAvatar
          });
        }
        
        batchCount++;
        totalUpdated++;

        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      // Update user document
      await updateDoc(userRef, {
        allPostsAnonymized: newAnonymizedState
      });

      setAllPostsAnonymized(newAnonymizedState);
      setIsAnonymizing(false);
      
      // Reload activity data
      loadUserActivityData();
    } catch (error) {
      console.error('Error updating post anonymization:', error);
      setIsAnonymizing(false);
      setShowErrorModal('Failed to update post anonymization. Please try again.');
    }
  };

  // Toggle anonymization for a single post
  const handleTogglePostAnonymization = async (activity, e) => {
    e.stopPropagation(); // Prevent navigation when clicking toggle
    
    if (!user?.uid) return;
    
    try {
      const postRef = doc(db, `venues/${activity.venueId}/discussions/${activity.id}`);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists() || postDoc.data().userId !== user.uid) {
        setShowErrorModal('You can only anonymize your own posts.');
        return;
      }

      const currentData = postDoc.data();
      const newAnonymizedState = !currentData.isAnonymous;
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userDataDoc = userDoc.exists() ? userDoc.data() : null;
      
      const DEFAULT_PROFILE_PHOTOS = [
        require('../../assets/profile_photos/koi.png'),
        require('../../assets/profile_photos/bear.png'),
      ];
      const getRandomDefaultPhoto = () => {
        const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
        return DEFAULT_PROFILE_PHOTOS[randomIndex];
      };

      if (newAnonymizedState) {
        await updateDoc(postRef, {
          isAnonymous: true,
          username: 'Anonymous',
          userAvatar: getRandomDefaultPhoto()
        });
      } else {
        const originalUsername = userDataDoc?.username || userDataDoc?.firstName || 'Anonymous';
        const originalAvatar = userDataDoc?.photoURL || getRandomDefaultPhoto();
        await updateDoc(postRef, {
          isAnonymous: false,
          username: originalUsername,
          userAvatar: originalAvatar
        });
      }

      // Reload activity data
      loadUserActivityData();
    } catch (error) {
      console.error('Error toggling post anonymization:', error);
      setShowErrorModal('Failed to update post. Please try again.');
    }
  };

  if (!user) return null;

  return (
    <>
    <Modal
      visible={showActivityLog}
      transparent={true}
      animationType="fade"
      onRequestClose={closeModal}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={closeModal}
      >
        <TouchableOpacity 
          style={styles.modalContainer}
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>My Community</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={closeModal}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Main Tab Navigation - Button Style */}
          <View style={styles.mainTabContainer}>
            {user?.userType === 'admin' && (
            <TouchableOpacity
              style={[
                styles.mainTabButton,
                activeTab === 'admin' && styles.activeMainTab
              ]}
              onPress={() => setActiveTab('admin')}
            >
              <Text style={[
                styles.mainTabText,
                activeTab === 'admin' && styles.activeMainTabText
              ]}>
                🛠️ Admin
              </Text>
            </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[
                styles.mainTabButton,
                activeTab === 'discussions' && styles.activeMainTab
              ]}
              onPress={() => setActiveTab('discussions')}
            >
              <Text style={[
                styles.mainTabText,
                activeTab === 'discussions' && styles.activeMainTabText
              ]}>
                💬 Discussions
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.mainTabButton,
                activeTab === 'invitations' && styles.activeMainTab
              ]}
              onPress={() => setActiveTab('invitations')}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text style={[
                  styles.mainTabText,
                  activeTab === 'invitations' && styles.activeMainTabText
                ]}>
                  📨 Invitations
                </Text>
                {/* Dynamic Invitations badge */}
                {organizationInvitations.length > 0 && (
                  <View style={styles.unreadBadge}>
                    <Text style={styles.unreadText}>
                      {organizationInvitations.length}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          </View>

          {/* Discussions Sub-tabs */}
          {activeTab === 'discussions' && (
            <View style={styles.subTabContainer}>
              <TouchableOpacity
                style={[
                  styles.subTab,
                  activeDiscussionTab === 'activity' && styles.activeSubTab
                ]}
                onPress={() => setActiveDiscussionTab('activity')}
              >
                <Text style={[
                  styles.subTabText,
                  activeDiscussionTab === 'activity' && styles.activeSubTabText
                ]}>
                  Activity Log
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.subTab,
                  activeDiscussionTab === 'messages' && styles.activeSubTab,
                  { flex: 1, flexDirection: 'row', alignItems: 'center' }
                ]}
                onPress={() => setActiveDiscussionTab('messages')}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={[
                    styles.subTabText,
                    activeDiscussionTab === 'messages' && styles.activeSubTabText
                  ]}>
                    Messages
                  </Text>
                  {/* Dynamic Unread badge */}
                  {ownedOrganizations.length === 0 && formatUnreadCount(totalUnreadCount) && (
                    <View style={styles.unreadBadge}>
                      <Text style={styles.unreadText}>
                        {formatUnreadCount(totalUnreadCount)}
                      </Text>
                    </View>
                  )}
                </View>
                {/* Sender Dropdown - Always show in discussions tab if user owns organizations */}
                {activeTab === 'discussions' && ownedOrganizations.length > 0 && (
                  <TouchableOpacity
                    style={styles.senderPhotoButton}
                    onPress={() => setShowSenderDropdown(!showSenderDropdown)}
                  >
                    <View style={{ position: 'relative' }}>
                      {selectedMessagingSender ? (
                        // Show organization photo
                        ownedOrganizations.find(o => o.id === selectedMessagingSender)?.photoURL ? (
                          <Image
                            source={{ uri: ownedOrganizations.find(o => o.id === selectedMessagingSender).photoURL }}
                            style={styles.senderPhoto}
                          />
                        ) : (
                          <View style={[styles.senderPhotoPlaceholder, { backgroundColor: '#FFEB3B' }]}>
                            <Text style={styles.senderPhotoPlaceholderText}>
                              {(ownedOrganizations.find(o => o.id === selectedMessagingSender)?.username || 
                                ownedOrganizations.find(o => o.id === selectedMessagingSender)?.title || 
                                'O')[0].toUpperCase()}
                            </Text>
                          </View>
                        )
                      ) : (
                        // Show user photo
                        user?.photoURL ? (
                          <Image
                            source={{ uri: user.photoURL }}
                            style={styles.senderPhoto}
                          />
                        ) : (
                          <View style={[styles.senderPhotoPlaceholder, { backgroundColor: '#4e7de0' }]}>
                            <Text style={styles.senderPhotoPlaceholderText}>
                              {(user?.username || user?.firstName || 'U')[0].toUpperCase()}
                            </Text>
                          </View>
                        )
                      )}
                      {/* Combined unread count badge on dropdown button */}
                      {formatUnreadCount(totalUnreadCount) && (
                        <View style={styles.senderPhotoUnreadBadge}>
                          <Text style={styles.senderPhotoUnreadText}>
                            {formatUnreadCount(totalUnreadCount)}
                          </Text>
                        </View>
                      )}
                    </View>
                    {/* Dropdown indicator */}
                    <View style={styles.senderPhotoIndicator}>
                      <Text style={styles.senderPhotoIndicatorText}>▼</Text>
                    </View>
                  </TouchableOpacity>
                )}
                
                {/* Dropdown Menu */}
                {showSenderDropdown && ownedOrganizations.length > 0 && (
                  <>
                    <View style={styles.senderDropdownMenu}>
                    <TouchableOpacity
                      style={[
                        styles.senderDropdownItem,
                        !selectedMessagingSender && styles.senderDropdownItemActive
                      ]}
                      onPress={() => {
                        setSelectedMessagingSender(null);
                        setShowSenderDropdown(false);
                      }}
                    >
                      <View style={{ position: 'relative' }}>
                        <Image
                          source={user?.photoURL ? { uri: user.photoURL } : require('../../assets/profile_photos/koi.png')}
                          style={styles.senderPhoto}
                        />
                        {/* Unread count badge for user */}
                        {formatUnreadCount(senderUnreadCounts[user?.uid] || 0) && (
                          <View style={styles.senderDropdownUnreadBadge}>
                            <Text style={styles.senderDropdownUnreadText}>
                              {formatUnreadCount(senderUnreadCounts[user?.uid] || 0)}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={[
                        styles.senderDropdownItemText,
                        !selectedMessagingSender && styles.senderDropdownItemTextActive
                      ]}>
                        {user?.username || 'You'}
                      </Text>
                    </TouchableOpacity>
                    {ownedOrganizations.map((org) => (
                      <TouchableOpacity
                        key={org.id}
                        style={[
                          styles.senderDropdownItem,
                          selectedMessagingSender === org.id && styles.senderDropdownItemActive
                        ]}
                        onPress={() => {
                          setSelectedMessagingSender(org.id);
                          setShowSenderDropdown(false);
                        }}
                      >
                        <View style={{ position: 'relative' }}>
                          <Image
                            source={org.photoURL ? { uri: org.photoURL } : require('../../assets/profile_photos/koi.png')}
                            style={styles.senderPhoto}
                          />
                          {/* Unread count badge for organization */}
                          {formatUnreadCount(senderUnreadCounts[org.id] || 0) && (
                            <View style={styles.senderDropdownUnreadBadge}>
                              <Text style={styles.senderDropdownUnreadText}>
                                {formatUnreadCount(senderUnreadCounts[org.id] || 0)}
                              </Text>
                            </View>
                          )}
                        </View>
                        <Text style={[
                          styles.senderDropdownItemText,
                          selectedMessagingSender === org.id && styles.senderDropdownItemTextActive
                        ]}>
                          {org.username || org.title || 'Organization'}
                        </Text>
                      </TouchableOpacity>
                    ))}
                    </View>
                  </>
                )}
              </TouchableOpacity>
            </View>
          )}

          {/* Invitations Sub-tabs */}
          {activeTab === 'invitations' && (
            <View style={styles.subTabContainer}>
              <TouchableOpacity
                style={[
                  styles.subTab,
                  activeInvitationTab === 'orgInvites' && styles.activeSubTab
                ]}
                onPress={() => setActiveInvitationTab('orgInvites')}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={[
                    styles.subTabText,
                    activeInvitationTab === 'orgInvites' && styles.activeSubTabText
                  ]}>
                    Organization Invites
                  </Text>
                  {/* Dynamic Invitations badge */}
                  {organizationInvitations.length > 0 && (
                    <View style={styles.unreadBadge}>
                      <Text style={styles.unreadText}>
                        {organizationInvitations.length}
                      </Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.subTab,
                  activeInvitationTab === 'inviteCodes' && styles.activeSubTab
                ]}
                onPress={() => setActiveInvitationTab('inviteCodes')}
              >
                <Text style={[
                  styles.subTabText,
                  activeInvitationTab === 'inviteCodes' && styles.activeSubTabText
                ]}>
                  Invite Codes
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Tab Content */}
          <ScrollView contentContainerStyle={styles.tabContent}>
            {activeTab === 'admin' ? (
              <View style={styles.adminContent}>
                {/* Search Bar */}
                <View style={styles.searchContainer}>
                  <TextInput
                    style={styles.searchInput}
                    placeholder="Search by venue, user, or message..."
                    placeholderTextColor="rgba(255, 255, 255, 0.6)"
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                  />
                </View>

                {/* Activity Stats */}
                <View style={styles.statsContainer}>
                  <Text style={styles.statsText}>
                    {isLoading ? 'Loading...' : `${getFilteredAdminActivity().length} activities ${searchQuery ? `(filtered from ${activityData.length})` : 'total'} - All Users (Last 7 days)`}
                  </Text>
                </View>
                
                {/* Activity List */}
                <View style={styles.activityList}>
                  {isLoading ? (
                    <Text style={styles.noActivityText}>Loading admin activity...</Text>
                  ) : getFilteredAdminActivity().length === 0 ? (
                    <Text style={styles.noActivityText}>
                      {searchQuery ? 'No matching admin activity found' : 'No admin activity found'}
                    </Text>
                  ) : (
                    getFilteredAdminActivity().map((activity, index) => (
                      <TouchableOpacity
                        key={`admin-${activity.venueId}-${activity.id}-${index}`}
                        style={styles.activityItem}
                        onPress={() => handleActivityClick(activity)}
                      >
                        <Text style={styles.activityVenue}>{activity.venueName}</Text>
                        <Text style={styles.activityMessage}>
                          <Text style={styles.activityUsername}>{activity.username || 'Anonymous'}</Text>: {activity.text?.substring(0, 150)}...
                        </Text>
                        <Text style={styles.activityTime}>
                          {activity.timestamp?.toLocaleString()}
                        </Text>
                      </TouchableOpacity>
                    ))
                  )}
                </View>
              </View>
            ) : activeTab === 'invitations' ? (
              <View style={styles.discussionsContent}>
                {activeInvitationTab === 'orgInvites' ? (
                  <View style={styles.adminContent}>
                    {/* Invitations Header */}
                    <View style={styles.statsContainer}>
                      <Text style={styles.statsText}>
                        {isLoadingInvitations 
                          ? 'Loading invitations...' 
                          : `${organizationInvitations.length} pending invitation${organizationInvitations.length !== 1 ? 's' : ''}`
                        }
                      </Text>
                    </View>

                    {/* Invitations List */}
                    <View style={styles.activityList}>
                      {isLoadingInvitations ? (
                        <View style={{ alignItems: 'center', marginTop: 40 }}>
                          <ActivityIndicator size="large" color="#4e7de0" />
                          <Text style={styles.noActivityText}>Loading invitations...</Text>
                        </View>
                      ) : organizationInvitations.length === 0 ? (
                        <Text style={styles.noActivityText}>
                          No pending invitations
                        </Text>
                      ) : (
                        organizationInvitations.map((invitation) => (
                          <View
                            key={invitation.id}
                            style={[styles.activityItem, { borderLeftColor: '#f1c40f' }]}
                          >
                            <View style={{ flex: 1 }}>
                              <Text style={[styles.activityVenue, { color: '#f1c40f' }]}>
                                {invitation.organizationName || 'Organization'}
                              </Text>
                              <Text style={styles.activityMessage}>
                                Invited by <Text style={[styles.activityUsername, { color: '#4e7de0' }]}>
                                  {invitation.inviterEmail}
                                </Text>
                              </Text>
                              {invitation.createdAt && (
                                <Text style={styles.activityTime}>
                                  {invitation.createdAt.toDate ? 
                                    invitation.createdAt.toDate().toLocaleString() : 
                                    'Recently'
                                  }
                                </Text>
                              )}
                            </View>
                            <View style={{ flexDirection: 'row', gap: 8, marginLeft: 8 }}>
                              <TouchableOpacity
                                style={{
                                  backgroundColor: '#2ecc71',
                                  padding: 10,
                                  borderRadius: 6,
                                  paddingHorizontal: 16,
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onPress={() => handleAcceptInvitation(invitation.id, invitation.organizationId)}
                              >
                                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>
                                  Accept
                                </Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={{
                                  backgroundColor: '#e74c3c',
                                  padding: 10,
                                  borderRadius: 6,
                                  paddingHorizontal: 16,
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onPress={() => handleRejectInvitation(invitation.id)}
                              >
                                <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>
                                  Reject
                                </Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        ))
                      )}
                    </View>

                    {/* Invitations History Section */}
                    {organizationInvitationsHistory.length > 0 && (
                      <>
                        <View style={[styles.statsContainer, { marginTop: 30, marginBottom: 15 }]}>
                          <Text style={styles.statsText}>
                            History ({organizationInvitationsHistory.length})
                          </Text>
                        </View>
                        <View style={styles.activityList}>
                          {organizationInvitationsHistory.map((invitation) => (
                            <View
                              key={invitation.id}
                              style={[
                                styles.activityItem, 
                                { 
                                  borderLeftColor: invitation.status === 'accepted' ? '#2ecc71' : '#e74c3c',
                                  opacity: 0.7
                                }
                              ]}
                            >
                              <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <Text style={[styles.activityVenue, { 
                                    color: invitation.status === 'accepted' ? '#2ecc71' : '#e74c3c'
                                  }]}>
                                    {invitation.organizationName || 'Organization'}
                                  </Text>
                                  <View style={{
                                    backgroundColor: invitation.status === 'accepted' ? '#2ecc71' : '#e74c3c',
                                    paddingHorizontal: 8,
                                    paddingVertical: 2,
                                    borderRadius: 4
                                  }}>
                                    <Text style={{ 
                                      color: '#fff', 
                                      fontSize: 10, 
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase'
                                    }}>
                                      {invitation.status}
                                    </Text>
                                  </View>
                                </View>
                                <Text style={styles.activityMessage}>
                                  Invited by <Text style={[styles.activityUsername, { color: '#4e7de0' }]}>
                                    {invitation.inviterEmail}
                                  </Text>
                                </Text>
                                {invitation.updatedAt && (
                                  <Text style={styles.activityTime}>
                                    {invitation.updatedAt.toDate ? 
                                      invitation.updatedAt.toDate().toLocaleString() : 
                                      invitation.createdAt?.toDate ? 
                                        invitation.createdAt.toDate().toLocaleString() : 
                                        'Recently'
                                    }
                                  </Text>
                                )}
                              </View>
                            </View>
                          ))}
                        </View>
                      </>
                    )}
                  </View>
                ) : (
                  <View style={styles.adminContent}>
                    {/* Invite Codes */}
                    {user?.isCommunityMember ? (
                      <InviteCodes />
                    ) : (
                      <View style={{ padding: 20 }}>
                        <CommunityMemberUpgradeForm
                          message="You must be a community member to access invite codes."
                          onSuccess={() => {
                            // Reload user data will happen automatically via useUser hook
                            setShowCommunityMemberSuccessModal(true);
                          }}
                          onError={(error) => {
                            console.error('Error upgrading to community member:', error);
                          }}
                        />
                      </View>
                    )}
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.discussionsContent}>
                {activeDiscussionTab === 'activity' ? (
                  <View style={styles.adminContent}>
                    {/* Search Bar */}
                    <View style={styles.searchContainer}>
                      <TextInput
                        style={styles.searchInput}
                        placeholder="Search by venue, user, or message..."
                        placeholderTextColor="rgba(255, 255, 255, 0.6)"
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                      />
                    </View>

                    {/* Activity Stats */}
                    <View style={styles.statsContainer}>
                      <Text style={styles.statsText}>
                        {isLoading ? 'Loading...' : `${getFilteredUserActivity().length} activities ${searchQuery ? `(filtered from ${userActivityData.length})` : 'total'} - Your Posts (Last 30 days)`}
                      </Text>
                    </View>

                    {/* Bulk Anonymization Toggle */}
                    <TouchableOpacity
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#434252',
                        padding: 12,
                        borderRadius: 8,
                        marginBottom: 10,
                        marginHorizontal: 10,
                        opacity: isAnonymizing ? 0.6 : 1,
                      }}
                      onPress={handleBulkAnonymizeToggle}
                      disabled={isAnonymizing}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        {isAnonymizing && (
                          <ActivityIndicator size="small" color="#fff" />
                        )}
                        <Text style={{ color: '#fff', fontSize: 14 }}>
                          {isAnonymizing 
                            ? (allPostsAnonymized ? 'Un-anonymizing...' : 'Anonymizing...')
                            : (allPostsAnonymized ? 'All Posts Anonymized' : 'All Posts Visible')
                          }
                        </Text>
                      </View>
                      <View style={{
                        width: 40,
                        height: 24,
                        backgroundColor: allPostsAnonymized ? '#2ecc71' : '#434252',
                        borderRadius: 12,
                        padding: 2,
                        justifyContent: 'center',
                        borderWidth: allPostsAnonymized ? 0 : 3,
                        borderColor: 'rgba(255,255,255,0.2)'
                      }}>
                        <View style={{
                          width: 20,
                          height: 20,
                          backgroundColor: '#fff',
                          borderRadius: 10,
                          transform: [{ translateX: allPostsAnonymized ? 16 : 0 }],
                        }} />
                      </View>
                    </TouchableOpacity>
                    
                    {/* Activity List */}
                    <View style={styles.activityList}>
                      {isLoading ? (
                        <Text style={styles.noActivityText}>Loading your activity...</Text>
                      ) : getFilteredUserActivity().length === 0 ? (
                        <Text style={styles.noActivityText}>
                          {searchQuery ? 'No matching posts found' : 'You haven\'t posted any discussions yet'}
                        </Text>
                      ) : (
                        getFilteredUserActivity().map((activity, index) => (
                          <View
                            key={`user-${activity.venueId}-${activity.id}-${index}`}
                            style={[styles.activityItem, { borderLeftColor: '#2ecc71' }]}
                          >
                            <TouchableOpacity
                              style={{ flex: 1 }}
                              onPress={() => handleActivityClick(activity)}
                            >
                              <Text style={styles.activityVenue}>{activity.venueName}</Text>
                              <Text style={styles.activityMessage}>
                                <Text style={[styles.activityUsername, { color: '#2ecc71' }]}>
                                  {activity.isAnonymous ? 'Anonymous' : 'You'}
                                </Text>: {activity.text?.substring(0, 150)}...
                              </Text>
                              <Text style={styles.activityTime}>
                                {activity.timestamp?.toLocaleString()}
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={{
                                padding: 8,
                                marginLeft: 8,
                                backgroundColor: activity.isAnonymous ? '#2ecc71' : '#666',
                                borderRadius: 4,
                                minWidth: 80,
                                alignItems: 'center'
                              }}
                              onPress={(e) => handleTogglePostAnonymization(activity, e)}
                            >
                              <Text style={{ color: '#fff', fontSize: 12 }}>
                                {activity.isAnonymous ? 'Un-anonymize' : 'Anonymize'}
                              </Text>
                            </TouchableOpacity>
                          </View>
                        ))
                      )}
                    </View>
                  </View>
                ) : (
                  <>
                    <MessagingComponent messagingSenderId={selectedMessagingSender} />
                  </>
                )}
              </View>
            )}
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>

    {/* Success Modal */}
    <Modal
      visible={showSuccessModal !== null}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowSuccessModal(null)}
    >
      <View style={styles.alertModalOverlay}>
        <View style={[styles.alertModalContent, styles.successModal]}>
          <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
          <Text style={styles.successTitle}>Success!</Text>

          <Text style={styles.successMessage}>{showSuccessModal}</Text>

          <TouchableOpacity
            onPress={() => setShowSuccessModal(null)}
            style={styles.successButton}
          >
            <Text style={styles.successButtonText}>OK</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>

    {/* Error Modal */}
    <Modal
      visible={showErrorModal !== null}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowErrorModal(null)}
    >
      <View style={styles.alertModalOverlay}>
        <View style={[styles.alertModalContent, styles.errorModal]}>
          <Ionicons name="alert-circle" size={48} color="#e74c3c" />
          <Text style={styles.errorTitle}>Error</Text>

          <Text style={styles.errorMessage}>{showErrorModal}</Text>

          <TouchableOpacity
            onPress={() => setShowErrorModal(null)}
            style={styles.errorButton}
          >
            <Text style={styles.errorButtonText}>OK</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>

    {/* Community Member Success Modal */}
    <Modal
      visible={showCommunityMemberSuccessModal}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowCommunityMemberSuccessModal(false)}
    >
      <View style={styles.alertModalOverlay}>
        <View style={[styles.alertModalContent, styles.successModal]}>
          <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
          <Text style={styles.successTitle}>Congratulations!</Text>

          <Text style={styles.successMessage}>You are now a community member.</Text>

          <TouchableOpacity
            onPress={() => setShowCommunityMemberSuccessModal(false)}
            style={styles.successButton}
          >
            <Text style={styles.successButtonText}>OK</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
    </>
  );
};

export default CommunityModal;

// Community Modal Styles
const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    ...(Platform.OS === 'web' && {
      cursor: 'default',
    }),
  },
  modalContainer: {
    width: '90%',
    maxWidth: 500,
    height: '100%',
    backgroundColor: '#000000',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    ...(Platform.OS === 'web' ? { boxShadow: '-2px 0 12px rgba(0,0,0,0.5)' } : {
      shadowColor: '#000',
      shadowOffset: { width: -2, height: 0 },
      shadowOpacity: 0.25,
      shadowRadius: 10,
      elevation: 10,
    }),
    ...(Platform.OS === 'web' && {
      cursor: 'auto',
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    color: '#ffffff',
    fontSize: 20,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 20,
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  // Main Tab Styles (Button Style)
  mainTabContainer: {
    flexDirection: 'row',
    padding: 15,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  mainTabButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  activeMainTab: {
    backgroundColor: '#4e7de0',
    borderColor: '#4e7de0',
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 6px rgba(78,125,224,0.6)' } : {
      shadowColor: '#4e7de0',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 4,
    }),
  },
  mainTabText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
  },
  activeMainTabText: {
    color: '#ffffff',
    fontWeight: 'bold',
  },
  
  // Sub Tab Styles (Under Discussions)
  subTabContainer: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    zIndex: 100,
  },
  subTab: {
    flex: 1,
    display: 'flex',
    minHeight: getResponsiveValue({
      mobile: 40,
      tablet: 40,
      laptop: 60,
      desktop: 60,
    }),
    paddingHorizontal: 15,
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    backgroundColor: 'transparent',
  },
  activeSubTab: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderBottomWidth: 2,
    borderBottomColor: '#4e7de0',
  },
  subTabText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    flexDirection: 'row',
    alignItems: 'center',
  },
  activeSubTabText: {
    color: '#ffffff',
  },
  unreadBadge: {
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    paddingHorizontal: 4,
  },
  unreadText: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  tabContent: {
    height: '100%',
    flex: 1,
  },
  adminContent: {
    flex: 1,
  },
  discussionsContent: {
    flex: 1,
    padding: 20,
    height: '100%'
  },
  searchContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  searchInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
  },
  statsContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  statsText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  activityList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 15,
  },
  activityItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#4e7de0',
  },
  activityVenue: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    marginBottom: 5,
  },
  activityMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    lineHeight: 20,
    marginBottom: 5,
  },
  activityUsername: {
    color: '#4e7de0',
    fontWeight: 'bold',
  },
  activityTime: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
  },
  noActivityText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    marginTop: 40,
  },
  senderPhotoButton: {
    position: 'relative',
    marginLeft: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'visible',
  },
  senderPhoto: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  senderPhotoPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  senderPhotoPlaceholderText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  senderPhotoIndicator: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#4e7de0',
    borderWidth: 2,
    borderColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  senderPhotoIndicatorText: {
    color: '#ffffff',
    fontSize: 8,
    fontWeight: 'bold',
  },
  senderPhotoUnreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: '#e74c3c',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#000000',
    zIndex: 10,
  },
  senderPhotoUnreadText: {
    color: '#ffffff',
    fontSize: 9,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  senderDropdownUnreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: '#e74c3c',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#000000',
    zIndex: 10,
  },
  senderDropdownUnreadText: {
    color: '#ffffff',
    fontSize: 9,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  senderDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 10,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    minWidth: 180,
    maxWidth: 250,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    zIndex: 1000,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    }),
  },
  senderDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  senderDropdownItemActive: {
    backgroundColor: 'rgba(78, 125, 224, 0.2)',
  },
  senderDropdownItemText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 13,
    fontFamily: 'VastagoGrotesk',
  },
  senderDropdownItemTextActive: {
    color: '#4e7de0',
    fontWeight: 'bold',
  },
  // Success/Error Modal Styles (similar to MediaManager)
  alertModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  alertModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  successModal: {
    alignItems: 'center',
  },
  successTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginTop: 10,
    marginBottom: 15,
    textAlign: 'center',
    color: '#2ecc71',
  },
  successMessage: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  successButton: {
    backgroundColor: '#2ecc71',
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
    alignItems: 'center',
  },
  successButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },
  errorModal: {
    alignItems: 'center',
  },
  errorTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginTop: 10,
    marginBottom: 15,
    textAlign: 'center',
    color: '#e74c3c',
  },
  errorMessage: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  errorButton: {
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
    alignItems: 'center',
  },
  errorButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },
});

